import * as ApiSettingService from "../../../../db/serviceApiSetting"
import { env } from "../../../../config/env"

/**
 * API client for merchant product endpoints
 */
export class MerchantProductApiClient {
  private baseUrl: string = env.SERVER_URL

  /**
   * Get JWT token from Firebase auth context
   */
  async getAuthToken(): Promise<string | null> {
    try {
      const accountAccess = await ApiSettingService.getAccess()
      if (!accountAccess) {
        console.error("Failed To Get Access Token")
        return null
      }
      return accountAccess.accessToken
    } catch (error) {
      console.error("Error retrieving access token:", error)
      return null
    }
  }

  /**
   * Make authenticated GET request
   */
  async get(endpoint: string, queryParams?: string): Promise<Response> {
    const token = await this.getAuthToken()
    if (!token) {
      throw new Error("No authentication token available")
    }

    const url = `${this.baseUrl}${endpoint}${queryParams ? '?' + queryParams : ''}`
    
    return fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      }
    })
  }

  /**
   * Make authenticated POST request
   */
  async post(endpoint: string, body: any): Promise<Response> {
    const token = await this.getAuthToken()
    if (!token) {
      throw new Error("No authentication token available")
    }

    const url = `${this.baseUrl}${endpoint}`
    
    return fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    })
  }

  /**
   * Make authenticated PUT request
   */
  async put(endpoint: string, body: any): Promise<Response> {
    const token = await this.getAuthToken()
    if (!token) {
      throw new Error("No authentication token available")
    }

    const url = `${this.baseUrl}${endpoint}`
    
    return fetch(url, {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify(body)
    })
  }

  /**
   * Parse error response and throw appropriate error
   */
  async parseErrorResponse(response: Response, defaultMessage: string): Promise<never> {
    if (response.status === 401) {
      throw new Error("Authentication failed - please log in again")
    }
    if (response.status === 403) {
      throw new Error("Access denied - insufficient permissions")
    }
    if (response.status === 404) {
      throw new Error("Resource not found")
    }
    if (response.status === 409) {
      // Try to get more specific conflict information
      try {
        const errorData = await response.json()
        if (errorData.message) {
          throw new Error(errorData.message)
        }
      } catch (e) {
        // Fall back to generic message
      }
      throw new Error("Conflict - resource already exists")
    }

    // Try to parse detailed error from server
    try {
      const errorData = await response.json()
      if (errorData.message) {
        let detailedMessage = errorData.message
        
        // Handle validation errors with field details
        if (errorData.error?.details?.field_errors) {
          const fieldErrors = Object.entries(errorData.error.details.field_errors)
            .map(([field, messages]: [string, any]) => 
              `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('; ')
          detailedMessage += ` - Field errors: ${fieldErrors}`
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          const details = errorData.errors
            .map((err: any) => `${err.field}: ${err.message}`)
            .join(", ")
          detailedMessage += `: ${details}`
        }
        
        throw new Error(detailedMessage)
      }
    } catch (e) {
      if (e instanceof Error && e.message !== defaultMessage) {
        throw e
      }
    }

    throw new Error(`${defaultMessage}: ${response.status} ${response.statusText}`)
  }
}

export default MerchantProductApiClient